import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Spinner,
  Text,
  HStack,
  IconButton,
  useToast,
  Center
} from '@chakra-ui/react';
import { MdZoomIn, MdZoomOut, MdDownload, MdFullscreen } from 'react-icons/md';

const DocumentViewer = ({ isOpen, onClose, documentUrl, fileName, fileType }) => {
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && documentUrl) {
      setLoading(true);
      setError(null);
      // Simulate loading time for iframe/image to load
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, documentUrl]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleDownload = () => {
    try {
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = fileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Download iniciado',
        description: 'O arquivo está sendo baixado.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Erro ao baixar',
        description: 'Não foi possível baixar o arquivo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFullscreen = () => {
    const elem = document.getElementById('document-content');
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    }
  };

  const renderDocument = () => {
    if (error) {
      return (
        <Center h="400px">
          <Box textAlign="center">
            <Text color="red.500" mb={4}>Erro ao carregar documento</Text>
            <Text>{error}</Text>
            <Button mt={4} onClick={handleDownload}>
              Baixar arquivo
            </Button>
          </Box>
        </Center>
      );
    }

    if (loading) {
      return (
        <Center h="400px">
          <Spinner size="xl" color="blue.500" />
          <Text ml={4}>Carregando documento...</Text>
        </Center>
      );
    }

    // Check if it's an image
    if (fileType && fileType.startsWith('image/')) {
      return (
        <Box 
          id="document-content"
          position="relative" 
          overflow="auto"
          maxH="70vh"
          bg="gray.50"
          borderRadius="md"
          p={4}
        >
          <img
            src={documentUrl}
            alt={fileName}
            style={{
              width: `${zoom}%`,
              height: 'auto',
              margin: '0 auto',
              display: 'block'
            }}
            onError={() => setError('Não foi possível carregar a imagem')}
          />
        </Box>
      );
    }

    // For PDFs and other documents, use iframe
    return (
      <Box 
        id="document-content"
        position="relative" 
        h="70vh"
        bg="gray.50"
        borderRadius="md"
      >
        <iframe
          src={documentUrl}
          title={fileName}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '0.375rem',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top left'
          }}
          onError={() => setError('Não foi possível carregar o documento')}
        />
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>
          <HStack justify="space-between">
            <Text>{fileName || 'Visualizar Documento'}</Text>
            <HStack spacing={2}>
              <IconButton
                icon={<MdZoomOut />}
                onClick={handleZoomOut}
                isDisabled={zoom <= 50}
                aria-label="Diminuir zoom"
                size="sm"
              />
              <Text fontSize="sm" minW="50px" textAlign="center">
                {zoom}%
              </Text>
              <IconButton
                icon={<MdZoomIn />}
                onClick={handleZoomIn}
                isDisabled={zoom >= 200}
                aria-label="Aumentar zoom"
                size="sm"
              />
              <IconButton
                icon={<MdFullscreen />}
                onClick={handleFullscreen}
                aria-label="Tela cheia"
                size="sm"
              />
              <IconButton
                icon={<MdDownload />}
                onClick={handleDownload}
                aria-label="Baixar documento"
                size="sm"
                colorScheme="blue"
              />
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {renderDocument()}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocumentViewer;